import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { SearchData } from '@/types/api';
import { useDebounce } from 'usehooks-ts';
import { EventPageAreaType, GTMEventName } from '@/constant/gtm';
import { useGTMEventTracker } from '@/hooks/gtm/useGTMEventTracker';

type UseSearchProps = {
  pageArea: EventPageAreaType;
  areaName: string;
  searchDocIdData?: string[];
  recentDocIDData?: string[];
  keywordData?: string[];
  onSubmit: ({ keyword, searchFields }: SearchData) => void;
  keywordDataLength: number;
  openApprovalModal: (props?: any) => void;
};

const useSearch = (props: UseSearchProps) => {
  const {
    pageArea,
    areaName,
    onSubmit,
    keywordDataLength,
    searchDocIdData,
    recentDocIDData,
    keywordData,
    openApprovalModal,
  } = props;
  const eventTracker = useGTMEventTracker(pageArea);
  const [docId, setDocId] = useState<string>();
  const [isFocus, setIsFocus] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const [items, setItems] = useState<number>(0);
  const methods = useFormContext<SearchData>();
  const { watch, setValue, register } = methods;
  const searchString = watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);
  const searchMode = debouncedValue.length > 1;

  useEffect(() => {
    setFocusedIndex(-1);
    if (debouncedValue.length > 0) {
      eventTracker(GTMEventName.searchKeyword, {
        area_name: areaName,
        keyword: debouncedValue,
      });
    }
  }, [debouncedValue]);

  const handleFocus = () => {
    setIsFocus(true);
  };
  const handleBlur = () => {
    inputRef.current?.blur();
    setIsFocus(false);
  };
  const handleSubmit = ({ keyword }: { keyword: string }) => {
    if (keyword.length === 1) return;

    setValue('keyword', keyword);
    onSubmit({ keyword, searchFields: watch('searchFields') });
    handleBlur();
  };

  const handleOpen = (id: string) => {
    setDocId(id);
    openApprovalModal(id);
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        setFocusedIndex((prev) => {
          return items > 0 ? (prev + 1) % items : -1;
        });
        break;

      case 'ArrowUp':
        setFocusedIndex((prev) =>
          items > 0 ? (prev - 1 < 0 ? items - 1 : prev - 1) : -1,
        );
        break;
      case 'Enter':
        if (searchMode) {
          if (focusedIndex === -1) {
            handleSubmit({ keyword: searchString });
          } else {
            const docId = searchDocIdData?.[focusedIndex];
            docId && handleOpen(docId);
            handleSubmit({ keyword: searchString });
          }
        } else {
          if (focusedIndex === -1) {
            handleSubmit({ keyword: searchString });
          } else if (focusedIndex < keywordDataLength) {
            keywordData && handleSubmit({ keyword: keywordData[focusedIndex] });
          } else {
            const docId =
              recentDocIDData &&
              recentDocIDData?.[focusedIndex - (keywordData?.length ?? 0)];
            docId && handleOpen(docId);
          }
        }
        break;
      case 'Escape':
        setFocusedIndex(-1);
        break;
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsFocus(false);
    }
  };

  const handleFocusBlur = () => {
    setFocusedIndex(-1);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return {
    handleKeyUp,
    t,
    menuRef,
    isFocus,
    setIsFocus,
    inputRef,
    handleFocus,
    searchMode,
    debouncedValue,
    watch,
    setValue,
    register,
    focusedIndex,
    handleSubmit,
    searchString,
    handleClickOutside,
    setItems,
    handleOpen,
    docId,
    handleFocusBlur,
  };
};
export default useSearch;
